import React, { useEffect, useState } from "react";
import moment from "moment";
import axios from "v2/utils/axios";
import { getAuthTokenFromCookie } from "lib/hooks";
import { LocalNotification } from "lib";
import { Button } from "components/Shared";
import { CheckBox, FieldHint } from "../shared";

interface Props {
  companyId: string;
}

const DEFAULT_PORT = "587";

// Stands in for a stored password. Deliberately a fixed run of bullets rather than
// the real length: a placeholder on a password input renders as plain text, so its
// width would leak how long the stored password is.
const PASSWORD_MASK = "•".repeat(8);

const VERIFY_TLS_HINT =
  "Check the relay's TLS certificate against its hostname and a trusted authority. Turn this off only " +
  "for a relay behind a self-signed or internally-issued certificate — it is what makes an encrypted " +
  "connection trustworthy rather than merely encrypted. Note it has no effect at all if the relay does " +
  "not offer STARTTLS, in which case the connection stays in plaintext either way.";

/**
 * Per-company outbound SMTP relay.
 *
 * When configured, notification email about this company's properties is sent
 * through this server instead of the TCPinpoint default. Mirrors the SSO section
 * above it: GET returns 404 until a configuration exists, so the section shows a
 * Create button first and an Update button afterwards.
 *
 * The password is write-only — the API never returns it. `passwordSet` tells us
 * whether one is stored, so the field can show a masked placeholder; submitting
 * the form with the password left blank keeps the stored one.
 */
const SmtpConfiguration: React.FC<Props> = ({ companyId }) => {
  const [persisted, setPersisted] = useState(false);
  // Health of the relay, from the save-time connection test and every send since.
  const [status, setStatus] = useState<string | null>(null);
  const [statusCheckedAt, setStatusCheckedAt] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [passwordSet, setPasswordSet] = useState(false);
  const [address, setAddress] = useState("");
  const [port, setPort] = useState(DEFAULT_PORT);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [domain, setDomain] = useState("");
  // Whether to check the relay's TLS certificate. Defaults on; off is the escape
  // hatch for a relay behind a self-signed or internally-issued certificate.
  const [verifyTls, setVerifyTls] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getAuthTokenFromCookie()}`,
  };
  const endpoint = `${process.env.REACT_APP_API_ENDPOINT_V2}admin/companies/${companyId}/smtp_server`;

  const applyStatus = (attributes: Record<string, any>) => {
    setStatus(attributes.status_label || null);
    setStatusCheckedAt(attributes.status_checked_at || null);
    setStatusError(attributes.status_error || null);
  };

  useEffect(() => {
    if (!companyId) return;

    axios
      .get(endpoint, { headers: authHeaders })
      .then((res) => {
        const attributes = res.data?.data?.attributes || {};
        setPersisted(true);
        setAddress(attributes.address || "");
        setPort(attributes.port ? String(attributes.port) : DEFAULT_PORT);
        setUserName(attributes.user_name || "");
        setDomain(attributes.domain || "");
        setVerifyTls(attributes.verify_tls !== false);
        setPasswordSet(!!attributes.password_set);
        applyStatus(attributes);
      })
      .catch((err) => {
        // 404 is the normal "not configured yet" case, not an error worth surfacing.
        if (err?.response?.status !== 404) {
          console.error("Error fetching SMTP configuration:", err);
        }
        setPersisted(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSave = () => {
    const payload: Record<string, any> = {
      smtp_server: {
        address: address,
        port: port,
        user_name: userName,
        domain: domain,
        verify_tls: verifyTls,
      },
    };
    // Only send the password when the admin actually typed one, so an untouched
    // field leaves the stored password alone.
    if (password) payload.smtp_server.password = password;

    setSaving(true);
    const request = persisted
      ? axios.put(endpoint, payload, { headers: authHeaders })
      : axios.post(endpoint, payload, { headers: authHeaders });

    request
      .then((res) => {
        const attributes = res.data?.data?.attributes || {};
        setPersisted(true);
        setPassword("");
        setPasswordSet(!!attributes.password_set);
        applyStatus(attributes);

        // The API runs a connection test after saving, so the response already
        // says whether the relay works. Report that rather than a bare "saved".
        if (attributes.status_label === "down") {
          const detail = attributes.status_error ? `: ${attributes.status_error}` : "";
          const message = `Saved, but the connection test failed${detail}`;
          new LocalNotification(message, message, "warning");
        } else {
          new LocalNotification("SMTP Configuration saved — connection test passed");
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.errors?.detail;
        const message = Array.isArray(detail) ? detail.join(", ") : detail || "Something Went Wrong";
        new LocalNotification(message, message, "error");
        console.error("Error saving SMTP configuration:", err);
      })
      .finally(() => setSaving(false));
  };

  const statusLabel = status === "up" ? "Up" : status === "down" ? "Down" : "Not tested";
  const showFailure = status === "down" && !!statusError;
  // Everything except the badge label lives in its tooltip, so the section header
  // stays to one line.
  const checkedLine = statusCheckedAt
    ? `Last checked ${moment(statusCheckedAt).fromNow()}.`
    : "Not checked yet — the connection is tested when you save, and after every email sent through it.";

  return (
    <div className="px-5 py-4 bg-white">
      <div className="gap-3 d-flex align-items-center mb-4">
        <h2 className="text-24 font-bold mb-0">SMTP Configuration</h2>
        {persisted && (
          <span className={`smtp-status smtp-status--${status || "unknown"}`} tabIndex={0}>
            <i className="fas fa-circle smtp-status__dot" aria-hidden="true" />
            {statusLabel}
            <span className="smtp-status__bubble" role="tooltip">
              {checkedLine}
              {showFailure && (
                <>
                  <br />
                  Last failure: {statusError}
                  <br />
                  Email is being sent through the TCPinpoint default mail server until this relay
                  recovers.
                </>
              )}
            </span>
          </span>
        )}
        <p className="text-14 mb-0">
          Send notification email about your properties through your own mail server. Leave this blank to
          use the TCPinpoint default.
        </p>
      </div>


      <label htmlFor="smtpAddress">SMTP server name (address)</label>
      <input
        id="smtpAddress"
        className="form-control"
        type="text"
        placeholder="e.g. smtp.example.com.au"
        value={address}
        onChange={(e: any) => setAddress(e.target.value)}
      />

      <label htmlFor="smtpPort" className="mt-3">
        Port
      </label>
      <input
        id="smtpPort"
        type="number"
        min={1}
        max={65535}
        className="form-control text-left"
        value={port}
        onChange={(e: any) => setPort(e.target.value)}
      />

      <label htmlFor="smtpUserName" className="mt-3">
        User name
      </label>
      <input
        id="smtpUserName"
        className="form-control"
        type="text"
        autoComplete="off"
        value={userName}
        onChange={(e: any) => setUserName(e.target.value)}
      />

      <label htmlFor="smtpPassword" className="mt-3">
        Password
      </label>
      <input
        id="smtpPassword"
        className="form-control"
        type="password"
        autoComplete="new-password"
        placeholder={passwordSet ? PASSWORD_MASK : ""}
        value={password}
        onChange={(e: any) => setPassword(e.target.value)}
      />

      <label htmlFor="smtpDomain" className="mt-3">
        Domain
      </label>
      <input
        id="smtpDomain"
        className="form-control"
        type="text"
        placeholder="e.g. example.com.au"
        value={domain}
        onChange={(e: any) => setDomain(e.target.value)}
      />

      {/* Label stays "Update" whether or not a configuration exists yet — the
          request method still switches between POST and PUT, but the distinction
          is an implementation detail the admin has no reason to care about.
          There is deliberately no delete: an SMTP configuration is not removable
          once created. To drop authentication, clear the user name — SmtpServer
          discards the password with it. */}
      <div className="mt-3 d-flex align-items-center">
        <CheckBox checked={verifyTls} onChange={() => setVerifyTls(!verifyTls)} />
        <span className="ml-2 text-16">Verify TLS certificate</span>
        <FieldHint text={VERIFY_TLS_HINT} />
      </div>

      <div className="mt-3 d-flex justify-content-end">
        <Button onClick={handleSave} disabled={saving}>
          Update SMTP Configuration
        </Button>
      </div>
    </div>
  );
};

export default SmtpConfiguration;
